import { HubRoleAssignmentMethod, PROJECT, ProjectRoles, projectRoleLabel } from 'app-constants'
import { IMemberInvite, IProjectMember, ITableWithCells, OptionProps } from 'types'
import { capitaliseFirstLetter } from 'helpers/utils'
import { formatShortDate } from 'helpers/date'
import { FilterGroup } from 'components/filter-menu'

// What the Members page may do to each member, and how it finds them. Only members added to the hub
// directly can have their role changed or be removed here; those with access through the hub's
// organisation or site keep the role given to them there. Invites not yet taken up are listed with the
// members, for Owners, as rows of their own.

/** A row of the Members table: a member, or an invite to someone who hasn't signed up to Morta yet. */
export type PersonRow =
  | { kind: 'member'; id: string; member: IProjectMember }
  | { kind: 'invite'; id: string; invite: IMemberInvite }

export const memberRow = (member: IProjectMember): PersonRow => ({ kind: 'member', id: member.user.publicId, member })
export const inviteRow = (invite: IMemberInvite): PersonRow => ({
  kind: 'invite',
  id: `invite-${invite.publicId}`,
  invite
})

/** The Access an invite shows, beside members' "direct", "organisation" and "site". */
export const INVITED = 'invited'

/** How the person has access: how the member came to be in the hub, or invited. */
export const rowAccess = (row: PersonRow): string => (row.kind === 'member' ? row.member.roleAssignedMethod : INVITED)

/** The row's name: the member's, or the email invited. */
export const rowName = (row: PersonRow): string => (row.kind === 'member' ? row.member.user.name : row.invite.email)

/** The row's hub role, as stored. */
export const rowRole = (row: PersonRow): string => (row.kind === 'member' ? row.member.role : row.invite.projectRole)

/** The roles a member can be given, most access first. */
export const ROLES = [ProjectRoles.OWNER, ProjectRoles.ADMIN, ProjectRoles.MEMBER]

/** What each role lets someone do in the hub, as inviting someone and changing a role describe it. */
export const ROLE_DESCRIPTIONS: Record<ProjectRoles, string> = {
  [ProjectRoles.OWNER]: `Can edit documents and tables, ${PROJECT} settings and member list.`,
  [ProjectRoles.ADMIN]: 'Can edit documents and tables.',
  [ProjectRoles.MEMBER]: 'Can view documents and tables but not edit.'
}

/** Owners and Builders need a paid-user seat (or an active free trial); Members don't. */
export const needsSeat = (role: string): boolean => role !== ProjectRoles.MEMBER

export const PAID_SEAT_REQUIRED = 'Paid seat required'

export const isDirect = (member: IProjectMember): boolean =>
  member.roleAssignedMethod === HubRoleAssignmentMethod.DIRECT

/** How the person has access: "Direct", "Organisation", "Site" or "Invited". */
export const accessLabel = (row: PersonRow): string => capitaliseFirstLetter(rowAccess(row))

/**
 * The roles the person viewing can choose for a member, their current one included; none when their role
 * can't be changed here. An Owner can give any role to any member added directly, except themselves (so
 * a hub is never left without the Owner who's changing it); a Builder can only make a Member a Builder.
 */
export const rolesFor = (
  member: IProjectMember,
  viewerRole: ProjectRoles | undefined,
  viewerEmail: string | undefined
): ProjectRoles[] => {
  if (!isDirect(member)) return []
  if (viewerRole === ProjectRoles.OWNER) return member.user.email === viewerEmail ? [] : ROLES
  if (viewerRole === ProjectRoles.ADMIN && member.role === ProjectRoles.MEMBER)
    return [ProjectRoles.ADMIN, ProjectRoles.MEMBER]
  return []
}

/** Owners can remove anyone added directly but other Owners; Builders can remove Members added directly. */
export const canRemove = (member: IProjectMember, viewerRole: ProjectRoles | undefined): boolean => {
  if (!isDirect(member)) return false
  if (viewerRole === ProjectRoles.OWNER) return member.role !== ProjectRoles.OWNER
  return viewerRole === ProjectRoles.ADMIN && member.role === ProjectRoles.MEMBER
}

/** A tag table's tags as dropdown options, grouped by their column and in order of name. */
export const tagOptions = (tagTable: ITableWithCells): OptionProps[] =>
  tagTable.cells
    .map((cell) => ({ label: cell.value, value: cell.id, group: cell.column.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

/** A tag table's tags for a filter menu: under a heading for each of their columns, in order of name. */
export const tagGroups = (tagTable: ITableWithCells): FilterGroup[] => {
  const options = tagOptions(tagTable)
  return [...new Set(options.map((option) => option.group))].map((group) => ({
    label: group,
    options: options
      .filter((option) => option.group === group)
      .map((option) => ({ value: option.value ?? '', label: option.label }))
  }))
}

/** The member's tags that come from the tag table given. */
export const memberTagsIn = (member: IProjectMember, tagTable: ITableWithCells) =>
  (member.user.tags ?? []).filter((tag) => tagTable.cells.some((cell) => cell.id === tag.referencePublicId))

/** The values of an invite's tags that come from the tag table given. */
export const inviteTagsIn = (invite: IMemberInvite, tagTable: ITableWithCells): string[] =>
  tagTable.cells
    .filter((cell) => invite.tags.some((tag) => tag.referencePublicId === cell.id))
    .map((cell) => cell.value)

/** The values of the row's tags from the tag table given, in order of name. */
export const rowTagsIn = (row: PersonRow, tagTable: ITableWithCells): string[] =>
  (row.kind === 'member'
    ? memberTagsIn(row.member, tagTable).map((tag) => tag.value)
    : inviteTagsIn(row.invite, tagTable)
  ).sort()

/**
 * Whether the invite has the text searched for in its email, role, "invited", who sent it, the day it was
 * sent or one of its tags.
 */
export const inviteMatches = (invite: IMemberInvite, search: string, tagTables: ITableWithCells[]): boolean => {
  const text = search.trim().toLowerCase()
  if (text === '') return true
  const fields = [
    invite.email,
    invite.projectRole,
    projectRoleLabel(invite.projectRole),
    INVITED,
    invite.invitedBy.name ?? '',
    invite.invitedBy.email,
    invite.createdAt.split('T')[0],
    formatShortDate(invite.createdAt),
    ...tagTables.flatMap((tagTable) => inviteTagsIn(invite, tagTable))
  ]
  return fields.some((field) => field.toLowerCase().includes(text))
}

/** Whether the row, a member or an invite, has the text searched for. */
export const rowMatches = (row: PersonRow, search: string, tagTables: ITableWithCells[]): boolean =>
  row.kind === 'member' ? memberMatches(row.member, search, tagTables) : inviteMatches(row.invite, search, tagTables)

/**
 * Whether the member has the text searched for in their name, email, role, how they have access, the day
 * they joined (as shown, "Oct 12, 2026", or as 2026-10-12) or one of their tags.
 */
export const memberMatches = (member: IProjectMember, search: string, tagTables: ITableWithCells[]): boolean => {
  const text = search.trim().toLowerCase()
  if (text === '') return true
  const fields = [
    member.user.name,
    member.user.email,
    member.role,
    projectRoleLabel(member.role),
    member.roleAssignedMethod,
    member.createdAt.split('T')[0],
    formatShortDate(member.createdAt),
    ...tagTables.flatMap((tagTable) => memberTagsIn(member, tagTable).map((tag) => tag.value))
  ]
  return fields.some((field) => field.toLowerCase().includes(text))
}

/**
 * Why the person viewing can't remove a member, said as what can be done instead; null when they can.
 * Follows canRemove.
 */
export const removeReason = (
  member: IProjectMember,
  viewerRole: ProjectRoles | undefined,
  viewerEmail: string | undefined
): string | null => {
  if (canRemove(member, viewerRole)) return null
  if (member.roleAssignedMethod === HubRoleAssignmentMethod.ORGANISATION)
    return "Organisation admins can be removed from this hub in the organisation's settings."
  if (member.roleAssignedMethod === HubRoleAssignmentMethod.SITE)
    return "Site admins can be removed from this hub in the site's settings."
  if (member.user.email === viewerEmail) return 'Another Owner can remove you from this hub.'
  if (member.role === ProjectRoles.OWNER)
    return 'An Owner can be removed once their role is changed to Builder or Member.'
  return 'Only Owners can remove Owners and Builders.'
}

/**
 * The note at the foot of a member's ⋯ menu, saying why Edit Role or Remove can't be chosen, and what can be
 * done instead; null when both can.
 */
export const memberMenuNote = (
  member: IProjectMember,
  viewerRole: ProjectRoles | undefined,
  viewerEmail: string | undefined
): string | null => {
  const canEditRole = rolesFor(member, viewerRole, viewerEmail).length > 0
  const removable = canRemove(member, viewerRole)
  if (canEditRole && removable) return null
  if (member.roleAssignedMethod === HubRoleAssignmentMethod.ORGANISATION)
    return "Organisation admins' role and membership can be managed in the organisation's settings."
  if (member.roleAssignedMethod === HubRoleAssignmentMethod.SITE)
    return "Site admins' role and membership can be managed in the site's settings."
  if (member.user.email === viewerEmail) return 'Another Owner can change your role or remove you from this hub.'
  if (!canEditRole && !removable) return 'Only Owners can change the role of Owners and Builders, or remove them.'
  return removeReason(member, viewerRole, viewerEmail)
}
